<script setup lang="ts">
import { ref, onMounted } from "vue";
import { breakpointsTailwind, useMediaQuery } from "@vueuse/core";
import type { CarouselApi } from "@/components/ui/carousel";

defineProps<{
  slides: {
    title: string;
    subtitle_1?: string;
    subtitle_2?: string;
    color: string;
    background_image?: string;
    image?: string;
    badge?: string;
  }[];
}>();

const isMobile = useMediaQuery(`(max-width: ${breakpointsTailwind.md}px)`);

const carouselApi = ref<CarouselApi>();
const selectedIndex = ref(0);
const scrollSnaps = ref<number[]>([]);

function setApi(api: CarouselApi) {
  carouselApi.value = api;
}

onMounted(() => {
  if (!carouselApi.value) return;

  scrollSnaps.value = carouselApi.value.scrollSnapList();
  selectedIndex.value = carouselApi.value.selectedScrollSnap();

  carouselApi.value.on("select", () => {
    if (!carouselApi.value) return;
    selectedIndex.value = carouselApi.value.selectedScrollSnap();
  });
});
</script>

<template>
  <Carousel class="w-full relative" @init-api="setApi">
    <CarouselContent>
      <CarouselItem
        v-for="slide in slides"
        :key="slide.title"
        :class="slide.color"
      >
        <div
          class="
            h-full
            bg-cover
            relative
            px-8
            pt-12
            xs:pt-18
            pb-0
            md:pb-16
          "
          :style="slide.background_image ? { backgroundImage: 'url(' + slide.background_image + ')' } : {}"
        >
          <Card>
            <CardContent class="container-3xl h-full flex flex-col gap-4 sm:gap-8 md:flex-row md:items-start md:gap-0">
              <div class="flex z-9 md:h-full flex-col justify-between w-3/5 md:pl-10">
                <div
                  :class="`
                    heading-2
                    text-${slide.color}-dark
                    lg:w-7/10
                  `"
                >
                  {{ slide.title }}
                </div>
                <div v-if="slide.subtitle_1 && slide.subtitle_2 && !isMobile" :class="`w-full bg-${slide.color} rounded-lg px-6 py-12`">
                  <p class="grotesk-5" :class="`text-${slide.color}-dark`">
                    {{ slide.subtitle_1 }}
                  </p>
                  <p class="heading-3" :class="`text-${slide.color}-dark`">
                    {{ slide.subtitle_2 }}
                  </p>
                </div>
              </div>
              <div class="relative w-full mx-[5%] md:w-3/5 md:-ml-[15%]">
                <div class="rounded-lg overflow-hidden aspect-3/4 mr-[15%] md:mr-0 lg:mr-[15%]">
                  <img
                    v-if="slide.image"
                    :src="slide.image"
                    class="w-full object-cover"
                  >
                </div>
                <div class="flex justify-end z-9 w-full relative -mt-[35%] -mr-[15%] md:mr-0">
                  <img
                    v-if="slide.badge"
                    :src="slide.badge"
                    class="aspect-square w-2/5"
                  >
                </div>
              </div>
              <div
                v-if="scrollSnaps.length > 1"
                class="static w-full md:absolute bottom-8 left-1/2 md:-translate-x-1/2 flex justify-center gap-3 z-10"
              >
                <button
                  v-for="(_, index) in scrollSnaps"
                  :key="index"
                  class="h-2 w-2 rounded-full transition-all"
                  :class="
                    index === selectedIndex
                      ? 'bg-white scale-110'
                      : 'bg-white/40 hover:bg-white/70'
                  "
                  aria-label="Aller à la diapositive"
                  @click="carouselApi?.scrollTo(index)"
                />
              </div>
              <div
                v-if="slide.subtitle_1 && slide.subtitle_2 && isMobile" :class="`bg-${slide.color} w-screen -ml-12 mt-6 p-6 md:hidden`"
              >
                <p class="grotesk-5" :class="`text-${slide.color}-dark`">
                  {{ slide.subtitle_1 }}
                </p>
                <p class="heading-3" :class="`text-${slide.color}-dark`">
                  {{ slide.subtitle_2 }}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </CarouselItem>
    </CarouselContent>
    <template v-if="slides.length > 1">
      <CarouselPrevious variant="ghost" class="text-white absolute left-4 top-1/2 -translate-y-1/2" />
      <CarouselNext variant="ghost" class="text-white absolute right-4 top-1/2 -translate-y-1/2" />
    </template>
  </Carousel>
</template>
